import React from 'react'
import { IoSettingsOutline, IoExpandOutline, IoOpenOutline, IoCloseOutline, IoMoveOutline } from 'react-icons/io5'
import type { PageId } from '../BottomNav'
import { WidgetSkeleton } from './WidgetSkeleton'

type SkeletonVariant = 'fixtures' | 'leagues' | 'picks' | 'alerts' | 'highlights' | 'trending'

interface WidgetProps {
  id: string
  title: string
  icon?: React.ReactNode
  expandable?: boolean
  linkTo?: PageId
  onRemove?: () => void
  onSettings?: () => void
  onNavigate?: (page: PageId) => void
  isEditing?: boolean
  isExpanded?: boolean
  onExpand?: () => void
  children: React.ReactNode
  className?: string
  headerActions?: React.ReactNode
  isLoading?: boolean
  error?: string | null
  skeletonVariant?: SkeletonVariant
}

export function Widget({
  id,
  title,
  icon,
  expandable,
  linkTo,
  onRemove,
  onSettings,
  onNavigate,
  isEditing,
  isExpanded,
  onExpand,
  children,
  className = '',
  headerActions,
  isLoading,
  error,
  skeletonVariant,
}: WidgetProps) {
  return (
    <div className={`widget ${isEditing ? 'widget--editing' : ''} ${isExpanded ? 'widget--expanded' : ''} ${className}`}>
      {/* Drag handle - only visible in edit mode */}
      {isEditing && (
        <div className="widget-drag-handle">
          <IoMoveOutline size={16} />
        </div>
      )}

      {/* Header */}
      <div className="widget-header">
        <div className="widget-header-left">
          {icon && <span className="widget-icon">{icon}</span>}
          <h3 className="widget-title">{title}</h3>
        </div>
        <div className="widget-header-right">
          {headerActions}
          {expandable && onExpand && (
            <button
              className="widget-action-btn"
              onClick={onExpand}
              title={isExpanded ? 'Collapse' : 'Expand'}
              aria-label={isExpanded ? 'Collapse' : 'Expand'}
            >
              <IoExpandOutline size={16} />
            </button>
          )}
          {linkTo && onNavigate && (
            <button
              className="widget-action-btn"
              onClick={() => onNavigate(linkTo)}
              title={`Open ${title}`}
              aria-label={`Open ${title}`}
            >
              <IoOpenOutline size={16} />
            </button>
          )}
          {onSettings && (
            <button
              className="widget-action-btn"
              onClick={onSettings}
              title="Settings"
              aria-label="Settings"
            >
              <IoSettingsOutline size={16} />
            </button>
          )}
          {isEditing && onRemove && (
            <button
              className="widget-action-btn widget-action-btn--danger"
              onClick={onRemove}
              title="Remove widget"
              aria-label="Remove widget"
            >
              <IoCloseOutline size={18} />
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="widget-body">
        {isLoading ? (
          skeletonVariant ? (
            <WidgetSkeleton variant={skeletonVariant} />
          ) : (
            <div className="widget-loading">
              <div className="widget-loading-spinner" />
            </div>
          )
        ) : error ? (
          <div className="widget-error">
            <p>{error}</p>
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  )
}
